import { Minus, Plus } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { calculatePlotData } from "src/helper/calculatePlotData";
import { StepThreeProps } from "src/interfaces/functionalComponentInterface";
import DownloadDialog from "./DownloadDialog";
import Expandable from "./Expandable";
import FeesPaymentDialog from "./FeesPaymentDialog";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const Review: React.FC<StepThreeProps> = ({
  startDate,
  numberOfClients,
  numberOfWeeks,
  weeksData,
  handleInputChange,
  handleWeekChange,
  totalClients,
  totalAccountValue,
  isActive,
  userName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [plotData, setPlotData] = useState<
    { month: string; Payment: number; Bonus: number }[]
  >([]);
  const [detailData, setDetailData] = useState<{
    [key: string]: { month: string; paymentAmount: number; bonus: number };
  }>({});
  const [tempWeeks, setTempWeeks] = useState<number | string>(numberOfWeeks);
  const [lastDate, setLastDate] = useState<string>("");

  const chartRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const plotData = calculatePlotData(startDate, weeksData);
    setPlotData(plotData.monthlyPayments);
    setDetailData(plotData.detail);
    setTempWeeks(numberOfWeeks);
    setLastDate(plotData.lastDate);
  }, [startDate, weeksData, numberOfWeeks]);

  return (
    <div
      className={`flex flex-col z-10 card-form ${
        isActive ? "show-form" : "hide-form"
      }`}
    >
      <p className="text-center text-sm md:text-base font-normal text-gray-600 mt-4">
        Here is what your first few months with Optimize will look like.
      </p>
      <div className="bg-reviewBg border border-[#D8D8D8] flex flex-col lg:flex-row justify-between items-end lg:w-4xl h-auto lg:h-28 mt-14 p-4 rounded-lg gap-2 lg:gap-6">
        <div className="flex flex-col lg:flex-row gap-x-24 gap-2 md:gap-6 w-full">
          <div className="flex flex-col justify-center items-center lg:items-center">
            <Label className="block text-sm md:text-base font-semibold lg:px-14">
              Start Date
            </Label>
            <Popover open={isOpen} onOpenChange={setIsOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full mt-2 text-sm md:text-base"
                >
                  {startDate.toLocaleDateString("en-US")}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="center" className="p-2 w-auto">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={(date) => {
                    if (date && date.getDay() !== 0 && date.getDay() !== 6)
                      handleInputChange(
                        "startDate",
                        date.toLocaleDateString("en-US")
                      );
                    setIsOpen(false);
                  }}
                  disabled={(date) =>
                    date.getDay() === 0 || date.getDay() === 6
                  }
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex flex-col justify-center items-center lg:justify-start lg:items-center">
            <Label className="block text-sm md:text-base font-semibold">
              Number of Weeks
            </Label>
            <div className="relative flex items-center w-full mt-2">
              <button
                onClick={() => {
                  const newValue = Math.max(0, Number(tempWeeks) - 1);
                  setTempWeeks(newValue);
                  handleInputChange("numberOfWeeks", newValue.toString());
                }}
                className={`absolute left-2 bg-primary hover:bg-primary-foreground rounded-full w-6 h-6 text-white flex items-center justify-center ${
                  Number(tempWeeks) <= 0 ? "opacity-50 pointer-events-none" : ""
                }`}
                disabled={Number(tempWeeks) <= 0}
              >
                <Minus size={18} />
              </button>

              <Input
                type="text"
                value={tempWeeks}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setTempWeeks(newValue === "" ? "" : parseInt(newValue) || 0);
                }}
                onBlur={() => {
                  const sanitizedValue = tempWeeks === "" ? 0 : tempWeeks;
                  setTempWeeks(sanitizedValue);
                  handleInputChange("numberOfWeeks", sanitizedValue.toString());
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                }}
                className="text-center text-sm md:text-base bg-white font-medium w-full"
              />

              <button
                onClick={() => {
                  const newValue = Number(tempWeeks) + 1;
                  setTempWeeks(newValue);
                  handleInputChange("numberOfWeeks", newValue.toString());
                }}
                className="absolute right-2 bg-primary hover:bg-primary-foreground rounded-full w-6 h-6 text-white flex items-center justify-center"
              >
                <Plus size={18} />
              </button>
            </div>
          </div>

          <div className="flex flex-col justify-center items-center lg:justify-start lg:items-center">
            <Label className="block text-sm md:text-base font-semibold">
              Number of Households
            </Label>

            <div className="w-full flex justify-center mt-3">
              <Label className="text-sm md:text-base font-semibold text-center">
                {totalClients.toLocaleString()}
              </Label>
            </div>
          </div>

          <div className="flex flex-col justify-center items-center lg:justify-start lg:items-center">
            <Label className="block text-sm md:text-base font-semibold">
              Total Account Value
            </Label>

            <div className="w-full flex justify-center mt-3">
              <Label className="text-sm md:text-base font-semibold text-center">
                {`$ ${totalAccountValue.toLocaleString()}`}
              </Label>
            </div>
          </div>
        </div>
        <div className="flex flex-col w-full lg:w-auto ">
          <div className="flex flex-col items-center lg:place-items-end w-full">
            <div className="relative flex mt-2 w-full gap-4">
              <FeesPaymentDialog
                isOpen={isDialogOpen}
                setIsOpen={setIsDialogOpen}
                totalClients={totalClients}
                totalAccountValue={totalAccountValue}
                numberOfWeeks={numberOfWeeks}
                detailData={detailData}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="mt-10 mb-4 lg:w-48">
        <DownloadDialog
          isOpen={isDownloadOpen}
          setIsOpen={setIsDownloadOpen}
          chartRef={chartRef}
          detailData={detailData}
          totalClients={totalClients}
          totalAccountValue={totalAccountValue}
          numberOfWeeks={numberOfWeeks}
          weeksData={weeksData}
          plotData={plotData}
          userName={userName}
          startDate={startDate}
          lastDate={lastDate}
        />
      </div>
      <div className="flex flex-col md:flex-row gap-6 mt-6 w-full md:w-4xl justify-between pb-10 md:min-h-[50vh]">
        <div className="flex-1">
          <div
            className="flex-1 bg-white rounded-lg border border-[#DFDFDF] p-2 text-xs md:text-sm targetDiv"
            style={{ height: "400px", minHeight: "400px" }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={plotData}
                margin={{ top: 20, right: 20, left: 40, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <Legend
                  verticalAlign="bottom"
                  align="center"
                  wrapperStyle={{ paddingTop: 40 }}
                  formatter={(value) =>
                    value === "Payment"
                      ? "Ongoing Monthly Payment"
                      : "Transition Bonus Payment"
                  }
                />
                <XAxis
                  dataKey="month"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                />
                <YAxis
                  tickFormatter={(value) => `$${value.toLocaleString()}`}
                  label={{
                    value: "Projected Monthly Compensation",
                    angle: -90,
                    position: "insideLeft",
                    offset: -25,
                    style: { textAnchor: "middle" },
                  }}
                />
                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                <Bar dataKey="Payment" stackId="a" fill="#2C3B4E" />
                <Bar dataKey="Bonus" stackId="a" fill="#5E8081" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div
            style={{
              position: "absolute",
              top: "-9999px",
              left: "-9999px",
              width: "1160px",
              height: "600px",
            }}
            className="bg-white"
            ref={chartRef}
          >
            <ResponsiveContainer width={1160} height={600}>
              <BarChart
                data={plotData}
                margin={{
                  top: 5,
                  right: 85,
                  left: 45,
                  bottom: 10,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <Legend
                  verticalAlign="bottom"
                  align="center"
                  wrapperStyle={{ paddingTop: 70, marginTop: 30, fontSize: 20 }}
                  formatter={(value) =>
                    value === "Payment"
                      ? "Ongoing Monthly Payment"
                      : "Transition Bonus Payment"
                  }
                />
                <XAxis
                  dataKey="month"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  tick={{ fontSize: 20 }}
                />
                <YAxis
                  tickFormatter={(value) => `$${value.toLocaleString()}`}
                  tick={{ fontSize: 20 }}
                  label={{
                    value: "Projected Monthly Compensation",
                    angle: -90,
                    position: "insideLeft",
                    offset: -37,
                    style: { textAnchor: "middle", fontSize: 20 },
                  }}
                />
                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                <Bar dataKey="Payment" stackId="a" fill="#2C3B4E" />
                <Bar dataKey="Bonus" stackId="a" fill="#5E8081" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-col md:flex-row gap-4 mt-4 mb-2">
            <div className="bg-[#F5F7FA] border border-[#D8D8D8] rounded-lg p-4 flex-1">
              <span className="font-semibold text-sm text-gray-800">
                Transition Bonus
              </span>
              <p className="text-xs text-gray-600 mt-2">
                Transition bonuses are paid out over the first 2 full calendar
                months where client households are fully onboarded.
              </p>
            </div>
            <div className="bg-[#F5F7FA] border border-[#D8D8D8] rounded-lg p-4 flex-1">
              <span className="font-semibold text-sm text-gray-800">
                Ongoing Monthly Payment
              </span>
              <p className="text-xs text-gray-600 mt-2">
                Ongoing monthly payments are calculated based on transferred
                account values as of the end of each month.
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 rounded-lg border border-[#DFDFDF] p-4">
          <div className="space-y-4 md:h-[45vh] overflow-y-auto">
            {weeksData.map((week, index) => (
              <Expandable
                key={index}
                title={`Week ${week.week}`}
                weekIndex={index}
                data={week}
                onChange={handleWeekChange}
                totalClients={totalClients}
                maxClients={numberOfClients}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Review;
